import type { CancelablePromise } from '/src/api';
import { OpenAPI, request as __request } from '/src/api';
import { Account, TCompanyId, TTransactionId } from '/src/lib/models';
import {
  TCreateAccountRequest,
  TCreateAccountResponse,
  TDeleteAccountResponse,
  TGetAccountResponse,
  TListAccountsResponse,
  TUpdateAccountRequest,
  TUpdateAccountResponse,
} from '/src/lib/types';

/**
 * Accounts Service
 * @desc Service for interacting with Lender Accounts
 */
export class AccountService {
  /**
   * List Accounts
   * @returns Account[]
   * @throws ApiError
   */
  public static async query(lenderId: TCompanyId): CancelablePromise<Account[]> {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/lenders/{lenderId}/account',
      path: { lenderId },
    })) as TListAccountsResponse;

    return res.data;
  }

  /**
   * Get Account
   * @param id TAccountId
   * @returns Account
   * @throws ApiError
   */
  public static async get(lenderId: TCompanyId, transactionId: TTransactionId): CancelablePromise<Account> {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/lenders/{lenderId}/account/{transactionId}',
      path: { lenderId, transactionId },
    })) as TGetAccountResponse;

    return res.data;
  }

  /**
   * Create Account
   * @param account Account
   * @returns Account
   * @throws ApiError
   */
  public static async create(lenderId: TCompanyId, req: TCreateAccountRequest): CancelablePromise<Account> {
    const res = (await __request(OpenAPI, {
      method: 'POST',
      url: '/lenders/{lenderId}/account',
      path: { lenderId },
      body: req,
    })) as TCreateAccountResponse;

    return res.data;
  }

  /**
   * Update Account
   * @param account Account
   * @returns Account
   * @throws ApiError
   */
  public static async update(
    lenderId: TCompanyId,
    transactionId: TTransactionId,
    req: TUpdateAccountRequest
  ): CancelablePromise<Account> {
    const res = (await __request(OpenAPI, {
      method: 'PUT',
      url: '/lenders/{lenderId}/account/{transactionId}',
      path: { lenderId, transactionId },
      body: req,
    })) as TUpdateAccountResponse;

    return res.data;
  }

  /**
   * Delete Account
   * @param id TAccountId
   * @returns Account
   * @throws ApiError
   */
  public static async remove(lenderId: TCompanyId, transactionId: TTransactionId): CancelablePromise<Account> {
    const res = (await __request(OpenAPI, {
      method: 'DELETE',
      url: '/lenders/{lenderId}/account/{transactionId}',
      path: { lenderId, transactionId },
    })) as TDeleteAccountResponse;

    return res.data;
  }
}
